import React from 'react';
import {
    Box, Card, CardContent, Collapse, IconButton, Paper, Stack, Table, TableBody, TableCell,
    TableContainer, TableHead, TableRow, Typography,
} from '@mui/material';
import KeyboardArrowDownIcon from '@mui/icons-material/KeyboardArrowDown';
import KeyboardArrowUpIcon from '@mui/icons-material/KeyboardArrowUp';
import { Column, ResponsiveTableProps, TableViewMode } from './responsiveTable.types';
import { useTableViewMode } from '../../hooks/useTableViewMode';
import TableViewSwitcher from './TableViewSwitcher';
import { ATTR, helpElement } from '../../help/attrs';

/**
 * Hides text visually while leaving it available to screen readers.
 *
 * Inlined rather than imported from `@mui/utils`: that package is only a transitive dependency of
 * `@mui/material` and is not declared in package.json, so importing it directly would break the
 * moment the dependency tree is hoisted differently.
 */
const visuallyHidden = {
    border: 0,
    clip: 'rect(0 0 0 0)',
    height: '1px',
    margin: -1,
    overflow: 'hidden',
    padding: 0,
    position: 'absolute',
    whiteSpace: 'nowrap',
    width: '1px',
} as const;

/** Help tags for a column label: a click explains the column on its own, also outside a table cell (card view). */
const columnHelp = <T,>(c: Column<T>): Record<string, string> =>
    c.helpKey ? { ...helpElement(c.helpKey), [ATTR.solo]: '' } : {};

export const leadColumns = <T,>(columns: Column<T>[]): Column<T>[] =>
    columns.filter(c => (c.priority ?? 2) === 1);

export const detailColumns = <T,>(columns: Column<T>[]): Column<T>[] =>
    columns.filter(c => (c.priority ?? 2) !== 1 && !c.hideInCard && c.role !== 'actions');

/** `role: 'actions'` columns, rendered separately in a card/compact footer — see module docs. */
export const actionColumns = <T,>(columns: Column<T>[]): Column<T>[] =>
    columns.filter(c => c.role === 'actions');

/**
 * Picks the lead/detail split for card and compact views.
 *
 * When the caller has set `priority: 1` on some columns, those lead and the rest (minus
 * `hideInCard` and `role: 'actions'`) are detail — exactly `leadColumns`/`detailColumns`. When
 * nobody set a priority, there is no lead at all, so this falls back to the first
 * `fallbackLeadCount` non-`hideInCard`, non-`role: 'actions'` columns as the lead, with everything
 * after that (again minus those two) as detail. That fallback is what keeps an unprioritised
 * table from rendering an unlabelled grid, or putting an action column in the headline/lead
 * position.
 */
const resolveLeadDetail = <T,>(columns: Column<T>[], fallbackLeadCount: number): { lead: Column<T>[]; detail: Column<T>[] } => {
    // leadColumns deliberately does no hideInCard/role filtering (its other callers rely on
    // that), so a priority: 1 actions column is excluded here rather than there — it must never
    // double-render as both the headline and the footer.
    const leads = leadColumns(columns).filter(c => c.role !== 'actions');
    if (leads.length > 0) {
        return { lead: leads, detail: detailColumns(columns) };
    }
    const visible = columns.filter(c => !c.hideInCard && c.role !== 'actions');
    return { lead: visible.slice(0, fallbackLeadCount), detail: visible.slice(fallbackLeadCount) };
};

type ActionNode = { key: string; node: React.ReactNode };

/**
 * A column's `render(row)` can legitimately come back falsy for a given row (e.g. "no Edit button
 * on a day with no punch") — knowing the actions COLUMN exists isn't the same as knowing this ROW
 * has anything to show. Rendering an empty footer box anyway (bordered, in cards; padded, in
 * compact) would leave a stray artifact with nothing in it, exactly what rule 5 forbids. Callers
 * compute this once per row and use its length to decide whether to render a footer at all, so a
 * column's render() only runs once per row rather than once for the check and again for display.
 */
const isEmptyNode = (node: React.ReactNode): boolean => {
    // React renders null/undefined/false/'' as nothing at all, and it renders the NUMBER 0 and
    // NaN as visible text — but a render() reaching for "no actions" almost certainly returned a
    // falsy placeholder, not a literal zero it wants shown in an actions footer. An empty array
    // is React's other invisible value and the natural return of `items.map(...)` over nothing.
    if (Array.isArray(node)) return node.every(isEmptyNode);
    if (typeof node === 'number') return node === 0 || Number.isNaN(node);
    return node === null || node === undefined || node === false || node === '';
};

const visibleActionNodes = <T,>(columns: Column<T>[], row: T): ActionNode[] =>
    columns
        .map(c => ({ key: c.key, node: c.render(row) }))
        .filter(({ node }) => !isEmptyNode(node));

/** Right-aligned row of already-rendered action outputs at the bottom of a card or expanded panel. */
const ActionsFooter = ({ nodes }: { nodes: ActionNode[] }) => (
    <Box
        data-testid="responsive-table-actions-footer"
        sx={{ display: 'flex', gap: 1, justifyContent: 'flex-end', mt: 1.25, pt: 1, borderTop: '1px solid', borderColor: 'divider' }}
    >
        {nodes.map(({ key, node }) => <React.Fragment key={key}>{node}</React.Fragment>)}
    </Box>
);

/**
 * Renders a column model as a table, a compact expandable list, or cards.
 *
 * Owns no data fetching, no filtering and no pagination — a page keeps all of that and hands this
 * component the rows it has already decided to show.
 *
 * `mode` deliberately has no destructuring default. Task 10 replaces the hardcoded `mode="table"`
 * callers with `mode ?? storedMode` from a saved user preference; if `mode` always resolved to a
 * concrete value here, that `??` could never fall through and the preference feature would be
 * dead code.
 */
function ResponsiveTable<T>({
    columns, rows, getRowKey, mode, pageKey, renderCard, emptyMessage = 'No records found',
    'data-help-section': helpSection,
}: ResponsiveTableProps<T>) {
    const { mode: storedMode, setMode } = useTableViewMode(pageKey);
    // Row keys are only unique within a table; aria-controls ids have to be unique in the
    // document, so scope them by page — two ResponsiveTables never share a pageKey.
    const detailsIdPrefix = `responsive-table-${pageKey}-details`;
    const resolved: TableViewMode = mode ?? storedMode;
    const [expanded, setExpanded] = React.useState<Record<string, boolean>>({});

    // Row keys recycle across pages and filters (index-derived keys, reused ids), so an expansion
    // left over from the previous row set can reopen an unrelated row. Every new row set starts
    // closed.
    //
    // Keyed on the array identity rather than on a signature of the keys, precisely because the
    // recycling case produces an IDENTICAL key list. Callers build `rows` fresh each render, so
    // an unrelated parent re-render can also collapse an open row — a cosmetic annoyance, and the
    // deliberate trade for never showing one record's details under another record's row.
    React.useEffect(() => { setExpanded({}); }, [rows]);

    const renderContent = (): React.ReactNode => {
        if (rows.length === 0) {
            return (
                <Paper sx={{ p: 3, textAlign: 'center' }}>
                    <Typography color="text.secondary">{emptyMessage}</Typography>
                </Paper>
            );
        }

        if (resolved === 'table') {
            return (
                <TableContainer component={Paper} data-testid="responsive-table-scroll">
                    <Table size="small">
                        <TableHead>
                            <TableRow>
                                {columns.map(c => (
                                    <TableCell key={c.key} align={c.align ?? 'left'} sx={{ fontWeight: 600, whiteSpace: 'nowrap' }} {...columnHelp(c)}>
                                        {c.header}
                                    </TableCell>
                                ))}
                            </TableRow>
                        </TableHead>
                        <TableBody>
                            {rows.map(row => (
                                <TableRow key={getRowKey(row)} hover>
                                    {columns.map(c => (
                                        <TableCell key={c.key} align={c.align ?? 'left'}>
                                            {c.render(row)}
                                        </TableCell>
                                    ))}
                                </TableRow>
                            ))}
                        </TableBody>
                    </Table>
                </TableContainer>
            );
        }

        if (resolved === 'cards') {
            const { lead: headline, detail: rest } = resolveLeadDetail(columns, 1);
            const actions = actionColumns(columns);

            return (
                <Stack spacing={1.25}>
                    {rows.map(row => (
                        <Card key={getRowKey(row)} variant="outlined">
                            <CardContent sx={{ p: 1.75, '&:last-child': { pb: 1.75 } }}>
                                {renderCard ? renderCard(row) : (
                                    <>
                                        <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', gap: 1, flexWrap: 'wrap' }}>
                                            {headline.map((c, i) => (
                                                <Box key={c.key} sx={{ fontWeight: i === 0 ? 600 : 400, fontSize: i === 0 ? '1rem' : '0.875rem' }}>
                                                    {c.render(row)}
                                                </Box>
                                            ))}
                                        </Box>
                                        {rest.length > 0 && (
                                            <Box sx={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: '7px 12px', mt: 1.25 }}>
                                                {rest.map(c => (
                                                    <Box key={c.key}>
                                                        <Box sx={{ color: 'text.secondary', fontSize: '0.65rem', textTransform: 'uppercase', letterSpacing: '.4px' }} {...columnHelp(c)}>
                                                            {c.cardLabel ?? c.header}
                                                        </Box>
                                                        <Box sx={{ fontSize: '0.875rem' }}>{c.render(row)}</Box>
                                                    </Box>
                                                ))}
                                            </Box>
                                        )}
                                        {(() => {
                                            const actionNodes = visibleActionNodes(actions, row);
                                            return actionNodes.length > 0 && <ActionsFooter nodes={actionNodes} />;
                                        })()}
                                    </>
                                )}
                            </CardContent>
                        </Card>
                    ))}
                </Stack>
            );
        }

        if (resolved === 'compact') {
            const { lead: shown, detail: hidden } = resolveLeadDetail(columns, 2);
            const actions = actionColumns(columns);

            return (
                <TableContainer component={Paper} data-testid="responsive-table-scroll">
                    <Table size="small">
                        <TableHead>
                            <TableRow>
                                {shown.map(c => (
                                    <TableCell key={c.key} align={c.align ?? 'left'} sx={{ fontWeight: 600 }} {...columnHelp(c)}>{c.header}</TableCell>
                                ))}
                                {/* The expander column still needs an accessible name; visually it stays blank. */}
                                <TableCell sx={{ width: 48 }}>
                                    <Box component="span" sx={visuallyHidden}>Details</Box>
                                </TableCell>
                            </TableRow>
                        </TableHead>
                        <TableBody>
                            {rows.map(row => {
                                const key = getRowKey(row);
                                const isOpen = Boolean(expanded[key]);
                                const panelId = `${detailsIdPrefix}-${key}`;
                                return (
                                    <React.Fragment key={key}>
                                        <TableRow hover>
                                            {shown.map(c => (
                                                <TableCell key={c.key} align={c.align ?? 'left'}>{c.render(row)}</TableCell>
                                            ))}
                                            <TableCell sx={{ p: 0.5 }}>
                                                <IconButton
                                                    size="small"
                                                    aria-label={isOpen ? 'Hide details' : 'Show details'}
                                                    aria-expanded={isOpen}
                                                    aria-controls={panelId}
                                                    data-help="common.tableView.expandRow"
                                                    onClick={() => setExpanded(prev => ({ ...prev, [key]: !prev[key] }))}
                                                >
                                                    {isOpen ? <KeyboardArrowUpIcon /> : <KeyboardArrowDownIcon />}
                                                </IconButton>
                                            </TableCell>
                                        </TableRow>
                                        <TableRow>
                                            <TableCell sx={{ p: 0, border: 0 }} colSpan={shown.length + 1}>
                                                <Collapse id={panelId} in={isOpen} timeout="auto" unmountOnExit>
                                                    <Box sx={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: '6px 12px', p: 1.5, bgcolor: 'action.hover' }}>
                                                        {hidden.map(c => (
                                                            <Box key={c.key}>
                                                                <Box sx={{ color: 'text.secondary', fontSize: '0.65rem', textTransform: 'uppercase', letterSpacing: '.4px' }} {...columnHelp(c)}>
                                                                    {c.cardLabel ?? c.header}
                                                                </Box>
                                                                <Box sx={{ fontSize: '0.875rem' }}>{c.render(row)}</Box>
                                                            </Box>
                                                        ))}
                                                    </Box>
                                                    {(() => {
                                                        const actionNodes = visibleActionNodes(actions, row);
                                                        return actionNodes.length > 0 && (
                                                            <Box sx={{ px: 1.5, pb: 1.5, bgcolor: 'action.hover' }}>
                                                                <ActionsFooter nodes={actionNodes} />
                                                            </Box>
                                                        );
                                                    })()}
                                                </Collapse>
                                            </TableCell>
                                        </TableRow>
                                    </React.Fragment>
                                );
                            })}
                        </TableBody>
                    </Table>
                </TableContainer>
            );
        }

        // All three modes (table, cards, compact) are handled above.
        return null;
    };

    return (
        <Box {...(helpSection ? { [ATTR.section]: helpSection } : {})}>
            {mode === undefined && (
                <Box sx={{ display: 'flex', justifyContent: 'flex-end', mb: 1 }}>
                    <TableViewSwitcher mode={resolved} onChange={setMode} />
                </Box>
            )}
            {renderContent()}
        </Box>
    );
}

export default ResponsiveTable;
